/**
 * Modelo de Ticket de soporte.
 *
 * Un ticket lo levanta cualquier usuario desde `Soporte → Soporte`. Al
 * crearse se manda un correo a la mesa de servicio. Después, el
 * Administrador Epione le pone el estimado de horas y va moviendo el estado.
 *
 * Espeja 1:1 la entidad `src/entities/supportTickets.py` del API.
 */

/**
 * Estados válidos del ticket. El backend valida contra esta misma lista.
 * El id interno ASIGNADO se muestra como "En proceso": al asignar responsable
 * el ticket pasa a En proceso (el valor guardado no cambia).
 */
export type TicketStatus = 'PENDIENTE' | 'ASIGNADO' | 'SOLUCIONADO';

export const TICKET_STATUSES: ReadonlyArray<{ id: TicketStatus; name: string; color: string }> = [
  { id: 'PENDIENTE',   name: 'Pendiente',   color: 'yellow' },
  { id: 'ASIGNADO',    name: 'En proceso',  color: 'blue'   },
  { id: 'SOLUCIONADO', name: 'Solucionado', color: 'green'  },
];

/** Tipo de ticket: SOFTWARE (se elige el módulo) o HARDWARE (la herramienta). */
export type TicketType = 'SOFTWARE' | 'HARDWARE';
export const TICKET_TYPES: ReadonlyArray<{ id: TicketType; name: string }> = [
  { id: 'SOFTWARE', name: 'Software' },
  { id: 'HARDWARE', name: 'Hardware' },
];
/** Herramientas de hardware que pueden fallar. */
export const HARDWARE_TOOLS: ReadonlyArray<string> = [
  'Widget', 'Bandeja lectora RFID', 'Handheld', 'Anaquel de RFID', 'Antena lectora RFID',
];

/** Severidad del ticket (Clasificación de incidentes · Soporte técnico §4.1). */
export type TicketPriority = 'CRITICA' | 'ALTA' | 'MEDIA' | 'BAJA';

/**
 * Tiempos por severidad. El "tiempo estimado" del ticket es su tiempo de
 * RESOLUCIÓN objetivo; `hours` es lo que se guarda en `estimatedHours`
 * (el API lo calcula igual a partir de la prioridad).
 */
export const TICKET_PRIORITIES: ReadonlyArray<{
  id: TicketPriority; name: string; description: string;
  response: string; resolution: string; hours: number; color: string;
}> = [
  { id: 'CRITICA', name: 'Crítica', description: 'Sistema caído, indisponibilidad total, o incidente con exposición real o potencial de PHI/ePHI', response: '15 minutos', resolution: '4 horas', hours: 4, color: 'red' },
  { id: 'ALTA',    name: 'Alta',    description: 'Funcionalidad crítica degradada; afecta a múltiples usuarios o clientes', response: '1 hora', resolution: '8 horas', hours: 8, color: 'orange' },
  { id: 'MEDIA',   name: 'Media',   description: 'Funcionalidad no crítica afectada; existe workaround disponible', response: '4 horas', resolution: '24 horas (1 día hábil)', hours: 24, color: 'yellow' },
  { id: 'BAJA',    name: 'Baja',    description: 'Consultas generales, solicitudes menores, dudas de uso', response: '1 día hábil', resolution: '3 días hábiles', hours: 72, color: 'green' },
];

export function ticketPriority(id: string | null | undefined) {
  return TICKET_PRIORITIES.find(p => p.id === id) ?? null;
}

export interface SupportTicket {
  id:                string;
  /** Folio corto autogenerado por el backend (SOP-XXXXXXXX). */
  folio:             string;
  subject:           string;
  description:       string;

  /** SOFTWARE o HARDWARE. */
  ticketType?:       TicketType | null;
  /** Módulo donde ocurre el problema (id del catálogo `modules`). */
  moduleId?:         string | null;
  /** Etiqueta visible del módulo, desnormalizada. */
  module:            string;
  /** Texto libre: en qué parte del módulo ocurre. */
  location?:         string | null;

  status:            TicketStatus;
  /** Severidad; define el tiempo estimado (= tiempo de resolución). */
  priority?:         TicketPriority | null;
  /** Estimado de horas que toma resolverlo (tiempo de resolución de la severidad). */
  estimatedHours?:   number | null;
  assignedTo?:       string | null;
  assignedToName?:   string | null;
  resolution?:       string | null;
  resolvedOn?:       string | null;

  /** Usuario que levantó el ticket (id, nombre y correo desnormalizados). */
  requestedBy?:      string | null;
  requestedByName?:  string | null;
  requestedByEmail?: string | null;

  organization?:     string;
  createdBy?:        string;
  /** Fecha de creación. La pone el backend con formato MM/DD/YYYY, HH:MM:SS. */
  createdOn?:        string;
  modifiedBy?:       string;
  modifiedOn?:       string;
  deleted?:          boolean;
}

/** Payload de alta. El backend genera id, folio, organization y fechas. */
export interface CreateSupportTicketPayload {
  subject:           string;
  description:       string;
  ticketType?:       TicketType | null;
  moduleId?:         string | null;
  module:            string;
  location?:         string | null;
  status?:           TicketStatus;
  priority?:         TicketPriority | null;
  requestedBy?:      string | null;
  requestedByName?:  string | null;
  requestedByEmail?: string | null;
  operationType?:    'single' | 'bulk';
}

/** Payload de actualización. Solo viajan los campos incluidos. */
export interface UpdateSupportTicketPayload {
  id:               string;
  subject?:         string;
  description?:     string;
  ticketType?:      TicketType | null;
  moduleId?:        string | null;
  module?:          string;
  location?:        string | null;
  status?:          TicketStatus;
  priority?:        TicketPriority | null;
  estimatedHours?:  number | null;
  assignedTo?:      string | null;
  assignedToName?:  string | null;
  resolution?:      string | null;
  resolvedOn?:      string | null;
  deleted?:         boolean;
  operationType?:   'single' | 'bulk';
}
